<template>
  <v-skeleton-loader :loading="loading" transition="scale-transition" type="table-tbody">
    <v-card class="mt-4 mx-auto">
      <v-sheet class="v-sheet--offset mx-auto" elevation="12" max-width="90%">
        <bar-chart :data="dataCollection" :options="options" />
      </v-sheet>
      <v-card-text v-if="percentDif > 0 && chartValues.length > 0" class="pt-0">
        <div class="title font-weight-light mb-2">Monthly Expenses</div>
        <v-icon class="mdi mdi-arrow-down theme--light green--text mb-1" size="15"></v-icon>
        <span class="green--text text--lighten 5">{{ percentDif }}%</span>
        <span class="subheading font-weight-light grey--text ml-1">Decrease in Expenses</span>
      </v-card-text>
      <v-card-text v-if="percentDif < 0 && chartValues.length > 0" class="pt-0">
        <div class="title font-weight-light mb-2">Monthly Expenses</div>
        <v-icon class="mdi mdi-arrow-up theme--light red--text mb-1" size="15"></v-icon>
        <span class="red--text text--lighten 5">{{ Math.abs(percentDif) }}%</span>
        <span class="subheading font-weight-light grey--text ml-1">Increase in Expenses</span>
      </v-card-text>
      <v-card-text v-if="percentDif === 0 && chartValues.length > 0" class="pt-0">
        <div class="title font-weight-light mb-2">Monthly Expenses</div>
        <v-icon class="mdi mdi-arrow-up theme--light red--text mb-1" size="15"></v-icon>
        <span class="subheading font-weight-light grey--text ml-1">No change in Expenses</span>
      </v-card-text>
      <v-card-text v-if="chartValues.length <= 1" class="pt-0">
        <div class="title font-weight-light mb-2">Monthly Expenses</div>
        <v-divider class="my-2"></v-divider>
        <v-alert type="info">No Data for selected input</v-alert>
      </v-card-text>
    </v-card>
  </v-skeleton-loader>
</template>

<script>
import axios from 'axios';
import BarChart from '../charts/BarChart';

export default {
  components: {
    BarChart,
  },
  props: [
    'organization',
    'insurance',
    'group',
    'groupObject',
    'providers',
    'locations',
    'startDate',
    'endDate',
    'visible',
  ],
  data() {
    return {
      dataCollection: null,
      chartMonths: [],
      chartValues: [],
      cancelSource: null,
      percentDif: 0,
      loading: false,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          xAxes: [
            {
              scaleLabel: {
                display: true,
                labelString: 'Months',
              },
              gridLines: {
                color: 'rgba(0, 0, 0, 0)',
              },
            },
          ],
          yAxes: [
            {
              scaleLabel: {
                display: true,
                labelString: 'Dollars',
              },
              gridLines: {
                color: 'rgba(0, 0, 0, 0)',
              },
            },
          ],
        },
      },
    };
  },
  mounted() {
    this.getData();
  },
  methods: {
    async getData() {
      if (this.visible === true) {
        this.loading = true;
        const org = this.organization;
        const group = this.groupObject;
        const groupNum = this.group;
        const pro = this.providers;
        const loc = this.locations;
        const ins = this.insurance;
        const start = this.startDate;
        const end = this.endDate;
        this.chartMonths = [];
        this.chartValues = [];
        let provNum = [];
        let locAddress = [];
        let url;
        if (pro.length > 0) {
          pro.forEach((prov) => {
            if (provNum.indexOf(prov.Center_PCP_Number) === -1) {
              provNum.push(prov.Center_PCP_Number);
            }
          });
        } else {
          provNum = [];
        }

        if (loc.length > 0) {
          loc.forEach((loca) => {
            if (locAddress.indexOf(loca.Center_Location) === -1) {
              locAddress.push(loca.Center_Location);
            }
          });
        } else {
          locAddress = [];
        }

        // set API route
        url = `api/kpi/groups=${groupNum}/providers=${provNum}/locs=${locAddress}/${ins}/${start}/${end}`;

        // Check for pending request and if so cancel old one for new incoming
        this.cancelSearch();
        // Set the CancelToken
        this.cancelSource = axios.CancelToken.source();

        const response = axios.get(url, { cancelToken: this.cancelSource.token }).then((response) => {
          this.loading = false;
          this.cancelSource = null;
          const { data } = response;
          data.forEach((info) => {
            this.chartMonths.push(info._id.month);
            this.chartValues.push(info.expenses);
          });
          this.getPercentDif();
        });
      }
    },
    chartData() {
      this.dataCollection = {
        labels: this.chartMonths,
        datasets: [
          {
            label: 'Expenses',
            backgroundColor: this.randomColor(),
            data: this.chartValues,
          },
        ],
      };
    },
    cancelSearch() {
      if (this.cancelSource) {
        this.cancelSource.cancel('Start new search, stop active search');
      }
    },
    randomColor() {
      const letters = '0123456789ABCDEF'.split('');
      let color = '#';
      for (let i = 0; i < 6; i++) {
        color += letters[Math.floor(Math.random() * 16)];
      }
      return color;
    },
    async getPercentDif() {
      // check for undefined values
      const firstVal = this.chartValues[0] ? this.chartValues[0] : 0;
      const lastVal = this.chartValues[this.chartValues.length - 1] ? this.chartValues[this.chartValues.length - 1] : 0;

      const decreaseValue = firstVal - lastVal;
      this.percentDif = firstVal !== 0
        ? Math.round((decreaseValue / firstVal) * 100)
        : Math.round(decreaseValue * 100);

      this.loading = false;
    },
  },
  watch: {
    async startDate() {
      3;

      if (this.insurance.length) {
        this.getData();
      }
    },
    async endDate() {
      if (this.insurance.length) {
        this.getData();
      }
    },
    async insurance() {
      if (this.insurance.length > 0 && this.group.length === 0) {
        this.getData();
      }
    },
    async group() {
      if (this.group.length > 0) {
        this.getData();
      }
    },
    async providers() {
      if (this.providers.length > 0) {
        this.getData();
      }
    },
    async locations() {
      if (this.locations.length > 0) {
        this.getData();
      }
    },
    async chartValues() {
      this.chartData();
    },
  },
};
</script>
